import type { Action, InputSource } from '@/core/input';
import { BUTTON_SLOTS, type ButtonSlot } from '@/core/settings';
import { type Vec2, clampLength } from '@/core/vec';
import { createSlotGlyph } from '@/ui/glyphs';

/**
 * Floating virtual stick plus an action cluster.
 *
 * "Floating" means the stick origin is wherever the thumb lands rather than a
 * fixed spot: on a phone you cannot see where your thumb is, and a fixed stick
 * means constantly re-finding it. Pointer events are tracked by pointerId so
 * moving and pressing a button simultaneously works.
 */

/** How far the nub may travel from the origin, matching the ring in CSS. */
const STICK_RADIUS = 44;

/**
 * Deflection at which movement reaches full speed, decoupled from the visual
 * radius.
 *
 * Thumbs do not travel the full radius of a stick they cannot see, so
 * normalising against STICK_RADIUS meant a typical drag asked for ~60% speed
 * and the character felt sluggish. Saturating earlier keeps the full analogue
 * range available for deliberate slow movement while making "walk normally"
 * the default.
 */
const STICK_FULL_TILT = 30;

/** Movement below this is treated as a tap, not a drag. */
const STICK_DEADZONE_PX = 5;

/**
 * Glyph and colour belong to the slot, not the action.
 *
 * A button's identity is where it sits and what it looks like — that is what
 * the thumb learns. Reassigning changes what the button *does*, exactly as
 * remapping a controller does; the triangle stays the triangle at the top.
 */
export const SLOT_APPEARANCE: Readonly<Record<ButtonSlot, { tint: string }>> = {
  top: { tint: '#c79bea' },
  right: { tint: '#5fc3e8' },
  bottom: { tint: '#e8705a' },
  left: { tint: '#8fd6a0' },
};

/** Human-readable action names, for screen readers and the settings panel. */
export const ACTION_NAMES: Readonly<Record<Action, string>> = {
  attack: 'Attack',
  dash: 'Dash',
  interact: 'Interact',
  special: 'Spirit',
  menu: 'Menu',
  nextTool: 'Next tool',
  nextItem: 'Next item',
  prevItem: 'Previous item',
};

export interface TouchControlsOptions {
  readonly container: HTMLElement;
  /** Which action each slot triggers. */
  readonly slots: Readonly<Record<ButtonSlot, Action>>;
}

export class TouchControls implements InputSource {
  private readonly root: HTMLDivElement;
  private readonly stickBase: HTMLDivElement;
  private readonly stickNub: HTMLDivElement;

  private stickPointer: number | null = null;
  private originX = 0;
  private originY = 0;
  private move: Vec2 = { x: 0, y: 0 };

  private readonly held = new Set<Action>();
  /** Which pointer is holding which button, so multi-touch releases correctly. */
  private readonly buttonPointers = new Map<number, Action>();

  /** Slot -> button, so a reassignment can relabel in place. */
  private readonly buttons = new Map<ButtonSlot, HTMLButtonElement>();
  private slots: Record<ButtonSlot, Action>;

  constructor(options: TouchControlsOptions) {
    const { container, slots } = options;
    this.slots = { ...slots };

    this.root = document.createElement('div');
    this.root.className = 'touch-controls';

    this.stickBase = document.createElement('div');
    this.stickBase.className = 'touch-stick';
    this.stickNub = document.createElement('div');
    this.stickNub.className = 'touch-stick-nub';
    this.stickBase.appendChild(this.stickNub);
    this.root.appendChild(this.stickBase);

    const cluster = document.createElement('div');
    cluster.className = 'touch-buttons';
    for (const slot of BUTTON_SLOTS) {
      const button = this.createButton(slot);
      this.buttons.set(slot, button);
      cluster.appendChild(button);
    }
    this.root.appendChild(cluster);

    // The stick zone covers the left half; the cluster sits on the right.
    const stickZone = document.createElement('div');
    stickZone.className = 'touch-stick-zone';
    this.root.appendChild(stickZone);

    suppressTouchGestures(stickZone);
    stickZone.addEventListener('pointerdown', this.onStickDown);
    stickZone.addEventListener('pointermove', this.onStickMove);
    stickZone.addEventListener('pointerup', this.onStickUp);
    stickZone.addEventListener('pointercancel', this.onStickUp);

    container.appendChild(this.root);
    this.setStickVisible(false);
  }

  /** Hide the overlay entirely on devices without touch. */
  static isTouchDevice(): boolean {
    return (
      typeof window !== 'undefined' &&
      ('ontouchstart' in window || navigator.maxTouchPoints > 0)
    );
  }

  readMove(): Vec2 {
    return this.move;
  }

  readHeld(): Iterable<Action> {
    return this.held;
  }

  destroy(): void {
    this.root.remove();
  }

  /** Apply a new assignment without rebuilding the DOM or losing held state. */
  setSlots(slots: Readonly<Record<ButtonSlot, Action>>): void {
    this.slots = { ...slots };
    // Anything held under the old mapping would otherwise stay stuck down.
    this.held.clear();
    this.buttonPointers.clear();
    for (const [slot, button] of this.buttons) {
      button.setAttribute('aria-label', ACTION_NAMES[this.slots[slot]]);
      button.classList.remove('is-pressed');
    }
  }

  private createButton(slot: ButtonSlot): HTMLButtonElement {
    const { tint } = SLOT_APPEARANCE[slot];

    const button = document.createElement('button');
    button.className = `touch-button touch-button--${slot}`;
    button.type = 'button';
    button.appendChild(createSlotGlyph(slot, 26));
    button.style.setProperty('--tint', tint);
    button.setAttribute('aria-label', ACTION_NAMES[this.slots[slot]]);

    // Resolved at press time, not capture time, so remapping takes effect
    // immediately rather than on the next rebuild.
    suppressTouchGestures(button);
    button.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      // State first, capture second. setPointerCapture throws if the browser
      // does not consider the pointer active, and doing it first meant one
      // throw swallowed the press entirely.
      const action = this.slots[slot];
      this.buttonPointers.set(event.pointerId, action);
      this.held.add(action);
      button.classList.add('is-pressed');
      capturePointer(button, event.pointerId);
    });

    const release = (event: PointerEvent): void => {
      const action = this.buttonPointers.get(event.pointerId);
      if (action === undefined) return;
      this.buttonPointers.delete(event.pointerId);
      this.held.delete(action);
      button.classList.remove('is-pressed');
    };
    button.addEventListener('pointerup', release);
    button.addEventListener('pointercancel', release);
    button.addEventListener('lostpointercapture', release);

    return button;
  }

  private readonly onStickDown = (event: PointerEvent): void => {
    event.preventDefault();
    if (this.stickPointer !== null) return;

    this.stickPointer = event.pointerId;
    this.originX = event.clientX;
    this.originY = event.clientY;

    this.stickBase.style.left = `${this.originX}px`;
    this.stickBase.style.top = `${this.originY}px`;
    this.setStickVisible(true);
    this.setNub(0, 0);

    // Capture last: it is an enhancement (tracking the thumb outside the zone),
    // not a precondition. A throw here must not cost us the whole gesture.
    capturePointer(event.currentTarget as HTMLElement, event.pointerId);
  };

  private readonly onStickMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.stickPointer) return;
    event.preventDefault();

    const dx = event.clientX - this.originX;
    const dy = event.clientY - this.originY;
    const distance = Math.hypot(dx, dy);

    if (distance < STICK_DEADZONE_PX) {
      this.move = { x: 0, y: 0 };
      this.setNub(0, 0);
      return;
    }

    this.move = clampLength({ x: dx / STICK_FULL_TILT, y: dy / STICK_FULL_TILT }, 1);
    const nub = clampLength({ x: dx, y: dy }, STICK_RADIUS);
    this.setNub(nub.x, nub.y);
  };

  private readonly onStickUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.stickPointer) return;
    this.stickPointer = null;
    this.move = { x: 0, y: 0 };
    this.setStickVisible(false);
  };

  private setStickVisible(visible: boolean): void {
    this.stickBase.style.opacity = visible ? '1' : '0';
  }

  private setNub(x: number, y: number): void {
    this.stickNub.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px)`;
  }
}

/**
 * Pointer capture keeps tracking a thumb that slides outside the element it
 * started on. It throws when the browser does not consider the pointer active,
 * which is a reason to lose that convenience — never a reason to lose the input.
 */
function capturePointer(element: HTMLElement, pointerId: number): void {
  try {
    element.setPointerCapture(pointerId);
  } catch {
    // Tracking continues via the document-level listeners either way.
  }
}

/**
 * Cancel the browser's own gestures on a control surface.
 *
 * `preventDefault` on a *pointer* event does not do this on iOS: pointer
 * events there are produced from touch events, so by the time one is
 * cancelled the touch has already been accepted as a tap — and two taps in
 * quick succession are a zoom, which is what happens when a thumb bounces on
 * the stick. Cancelling `touchstart` is what actually stops it.
 *
 * Safe on these elements specifically because none of them use `click`: the
 * stick and the buttons are driven by pointerdown/pointerup, and preventing
 * the touch default only suppresses the synthesised mouse events they do not
 * listen for.
 */
function suppressTouchGestures(element: HTMLElement): void {
  element.addEventListener(
    'touchstart',
    (event) => {
      event.preventDefault();
    },
    // passive:false — a passive listener cannot preventDefault, and browsers
    // default touchstart to passive.
    { passive: false },
  );
}
